/**
 * travauxDeParagraphe — ce que les verbes qui écrivent des paragraphes ENTIERS
 * (`format_paragraphs`, sa remise à zéro, `copy_format`) font de la même façon
 * une fois décidé QUOI écrire sur chaque paragraphe : une étape de plan par
 * paragraphe, puis une série d'écritures hors plan, puis les comptes du reçu.
 *
 * WHY à côté du socle et pas dedans : `ecritureDeForme.ts` lie UNE écriture à
 * l'état du document (révision, fichier exporté, peintre) ; ce module enchaîne
 * ces écritures sur une liste de paragraphes et tient ce que chacun a reçu. Il
 * ne sait rien de l'éditeur : tout fait mesuré reste dans l'en-tête du socle.
 */

import type { CanvasActionReceipt } from './actionReceipt';
import type { ParagrapheCible } from './cibleDeParagraphes';
import type { EcritureDeFormeOuverte } from './ecritureDeForme';
import { adresseHorsDuCorps, blocDeLEcriture } from './horsDuCorps';
import { RELANCE_LE_PEINTRE } from './peintreDeLEditeur';
import { enLots, etapeDuPlan, ETAPES_MAX_PAR_PLAN, type EtapeDePlan } from './planDeFormatText';
import type { FaitsDeLEcritureDeForme, RefusDeLEditeur } from './recuDEcritureDeForme';
import type { EcrituresDeParagraphe } from './reglagesDeParagraphe';
import { isolerLesEtapes, raisonDuRefus, risqueUnRefusDeTexte, type RaisonDeSaut } from './repliDeLot';

/** Une méthode de l'API Document, appelée sur sa surface. */
export type MethodeProvider = (input?: unknown, options?: unknown) => unknown;

/**
 * Un paragraphe de la cible, et ce qu'il a reçu. Chaque verbe y ajoute ce que
 * le paragraphe doit recevoir, et resserre `faites` à ses propres familles.
 */
export interface Travail {
  paragraphe: ParagrapheCible;
  /** Les familles d'écriture que l'éditeur a appliquées, ou tenues pour sans objet (`NO_OP`). */
  faites: Set<string>;
  /**
   * Les familles dont une écriture au moins a réellement changé le paragraphe : une écriture sans objet n'y entre
   * pas, une famille ENTAMÉE puis interrompue (une bordure dont un côté manque) y entre. C'est ce qui dit qu'un
   * paragraphe est écrit en partie, et qu'une révision en attente a reçu une écriture.
   */
  appliquees: Set<string>;
  /** Présent quand l'éditeur refuse l'étape de plan du paragraphe : plus rien n'y est écrit, et voici pourquoi. */
  saute?: RaisonDeSaut;
}

/** Une famille d'écriture d'un verbe, et si un travail doit la recevoir. */
export interface FamilleDeTravail<T extends Travail> {
  famille: string;
  prevue: (travail: T) => boolean;
}

/** Vrai si le travail a reçu toutes les écritures qu'il devait recevoir. */
export const estEntier = <T extends Travail>(travail: T, familles: ReadonlyArray<FamilleDeTravail<T>>): boolean => (
  familles.every(({ famille, prevue }) => !prevue(travail) || travail.faites.has(famille))
);

/**
 * Une étape `format.apply` de bloc par travail, par lots de
 * {@link ETAPES_MAX_PAR_PLAN}. L'identifiant d'une étape donne le rang de son
 * travail ({@link etapeDuPlan}) : une étape soumise seule garde son paragraphe.
 * `nodeType` est exigé par la forme de l'étape mais ne décide pas du bloc
 * visé : l'identifiant suffit (`formatParagraphsExecutor.ts`).
 */
function etapesParLot<T extends { paragraphe: ParagrapheCible }>(travaux: readonly T[], versEtape: (travail: T) => Record<string, unknown>): EtapeDePlan[][] {
  return enLots(travaux.map((travail, rang): EtapeDePlan => ({
    id: `p${rang}`,
    op: 'format.apply',
    where: { by: 'block', nodeType: 'paragraph', nodeId: travail.paragraphe.nodeId },
    args: { ...versEtape(travail), scope: 'block' },
    ...adresseHorsDuCorps(travail.paragraphe),
  })), ETAPES_MAX_PAR_PLAN);
}

/** Ce qu'un verbe dit de son étape de plan. */
export interface PlanParParagraphe<T extends Travail> {
  /** La famille que l'étape écrit : dite faite sur chaque travail dont l'étape est appliquée, ou sans objet. */
  famille: string;
  /** Les arguments de l'étape d'un travail (`inline`, `alignment`) ; `scope:'block'` y est ajouté. */
  versEtape: (travail: T) => Record<string, unknown>;
  /** Vrai si l'étape du travail écrit une forme de TEXTE : là où l'éditeur risque de la refuser, elle part seule (`repliDeLot.ts`). Absent : aucune. */
  ecritDuTexte?: (travail: T) => boolean;
  /** Ce que le plan écrit, pour le message d'un aperçu illisible (« alignment », « text reset »). */
  nom: string;
  /** Nom de l'étape dans les traces. */
  etiquette: string;
  /** Vrai si des écritures par paragraphe suivent le dernier plan. */
  dAutresEcrituresSuivent: boolean;
}

/**
 * Écrit par plans atomiques une étape par travail : aperçu de TOUS les lots
 * avant la première écriture, étapes à risque soumises seules, application,
 * puis le sort de chaque étape reporté sur son travail — `faites` pour une
 * étape appliquée ou sans objet, `saute` avec la raison à redire pour une
 * étape que l'éditeur refuse, à l'aperçu ou à l'application.
 *
 * @param travaux Les travaux qui ont une étape de plan, dans l'ordre du document.
 * @returns Le nombre de plans soumis et d'étapes écrites seules ; ou le reçu du refus d'un aperçu, rien n'étant écrit.
 */
export async function ecrireParPlans<T extends Travail>(
  ecriture: Pick<EcritureDeFormeOuverte, 'apercevoir' | 'appliquerLesPlans'>,
  travaux: readonly T[],
  plan: PlanParParagraphe<T>,
): Promise<{ refus: CanvasActionReceipt } | { plans: number; ecritesSeules: number }> {
  const sauter = (stepId: string, refus: RefusDeLEditeur): void => {
    const travail = etapeDuPlan(travaux, stepId);
    if (travail !== undefined) travail.saute = raisonDuRefus(refus, travail.paragraphe.porte);
  };
  const acceptes: EtapeDePlan[][] = [];
  for (const steps of etapesParLot(travaux, plan.versEtape)) {
    const apercu = await ecriture.apercevoir(steps, plan.nom);
    if ('refus' in apercu) return apercu;
    for (const { stepId, refus } of apercu.refusees) sauter(stepId, refus);
    acceptes.push(apercu.gardees);
  }
  const aRisque = (step: EtapeDePlan): boolean => {
    const travail = etapeDuPlan(travaux, step.id);
    return travail !== undefined && plan.ecritDuTexte?.(travail) === true && risqueUnRefusDeTexte(travail.paragraphe.porte);
  };
  const lots = isolerLesEtapes(acceptes, aRisque);

  const sorts = await ecriture.appliquerLesPlans(lots, plan.etiquette, { dAutresEcrituresSuivent: plan.dAutresEcrituresSuivent });
  for (const sort of sorts) {
    // Une étape que l'éditeur dit sans effet laisse le paragraphe dans l'état demandé : elle est tenue pour faite,
    // sans avoir rien changé.
    for (const stepId of sort.ecrites) etapeDuPlan(travaux, stepId)?.appliquees.add(plan.famille);
    for (const stepId of [...sort.ecrites, ...sort.sansObjet]) etapeDuPlan(travaux, stepId)?.faites.add(plan.famille);
    for (const { stepId, refus } of sort.sautees) sauter(stepId, refus);
  }
  return {
    plans: lots.length,
    ecritesSeules: lots.filter((lot, rang) => lot.length === 1 && lot.every(aRisque) && (sorts[rang]?.ecrites.length ?? 0) > 0).length,
  };
}

/** Une écriture hors plan sur un paragraphe : sa famille, son nom dans les traces, l'appel à l'éditeur, et ce qu'elle fait d'un peintre arrêté. */
export interface EcritureHorsPlan {
  famille: string;
  etiquette: string;
  appel: Parameters<EcritureDeFormeOuverte['ecrireUnParagraphe']>[1];
  relanceLePeintre: boolean;
}

/** Les écritures par paragraphe que `format_paragraphs` et `copy_format` ont en commun (`mesure-format-paragraphs`, `mesure-format-paragraphs-bordure-reset`). */
export interface SurfaceDeParagraphe {
  setIndentation?: MethodeProvider;
  setSpacing?: MethodeProvider;
  setBorder?: MethodeProvider;
  clearBorder?: MethodeProvider;
  setShading?: MethodeProvider;
  clearShading?: MethodeProvider;
}

/** Les surfaces de l'éditeur que les écritures hors plan d'un paragraphe empruntent. */
export interface SurfacesHorsPlan {
  /** `styles.paragraph` : le style nommé se pose par `setStyleRef`, qui garde la forme posée à la main (`reglagesDeParagraphe.ts`, « Le style nommé »). */
  styles?: { setStyleRef?: MethodeProvider };
  /** `format.paragraph`. */
  paragraphe?: SurfaceDeParagraphe;
  /** `tables` : le fond et le cadre de la cellule d'un paragraphe (`reglagesDeParagraphe.ts`, « Le fond », « Le cadre d'une case de tableau »). */
  tables?: { setShading?: MethodeProvider; clearShading?: MethodeProvider; setBorders?: MethodeProvider };
}

/**
 * Les écritures hors plan d'un paragraphe, dans l'ordre où les soumettre : le
 * style nommé d'abord, puis les retraits, l'espacement, la bordure côté par
 * côté, le fond du paragraphe, puis celui de sa cellule de tableau et le cadre
 * de cette cellule. Le style passe
 * en premier parce que les autres sont décidées sur ce
 * que le paragraphe montre une fois ce style posé : une série interrompue
 * laisse ainsi un style sans ses retraits, son espacement ou sa bordure, jamais
 * ceux-ci calculés pour un style absent.
 *
 * L'alignement n'est pas de cette série : il part en plan AVANT elle
 * (`formatParagraphsExecutor.ts`, « Un style nommé, seul ou avec d'autres
 * réglages »). Une série interrompue après le plan laisse donc, sur les
 * paragraphes que le style n'a pas atteints, l'alignement posé à la main pour
 * le style demandé, sous leur style d'avant. Le reçu le dit
 * (`FORMAT_WRITE_INTERRUPTED`) : ces paragraphes sont comptés écrits en partie
 * (`format.paragraphsPartlyWritten`), l'avancement est donné famille par
 * famille (« named style written on 1 of 3 paragraphs, alignment written on 3
 * of 3 paragraphs »), et aucun réglage n'est dit conforme.
 *
 * Les méthodes sont appelées SUR leur surface, jamais détachées : rien ne
 * garantit qu'une méthode du fournisseur reste liée une fois extraite.
 */
export function ecrituresHorsPlanDuParagraphe(
  { styles, paragraphe: surface, tables }: SurfacesHorsPlan,
  cible: Pick<ParagrapheCible, 'nodeId' | 'partie'>,
  { style, retraits, espacement, bordure, fond, fondDeCellule, cadreDeCase }: Pick<EcrituresDeParagraphe, 'style' | 'retraits' | 'espacement' | 'bordure' | 'fond' | 'fondDeCellule' | 'cadreDeCase'>,
): EcritureHorsPlan[] {
  const { nodeId } = cible;
  const target = blocDeLEcriture(cible);
  return [
    ...(style === undefined ? [] : [{
      famille: 'style',
      etiquette: `style:${nodeId}`,
      appel: (options: unknown) => styles?.setStyleRef?.({ target, styleId: style }, options),
      relanceLePeintre: RELANCE_LE_PEINTRE.styleNomme,
    }]),
    ...(retraits === undefined ? [] : [{
      famille: 'retraits',
      etiquette: `retraits:${nodeId}`,
      appel: (options: unknown) => surface?.setIndentation?.({ target, ...retraits }, options),
      relanceLePeintre: RELANCE_LE_PEINTRE.retraits,
    }]),
    ...(espacement === undefined ? [] : [{
      famille: 'espacement',
      etiquette: `espacement:${nodeId}`,
      appel: (options: unknown) => surface?.setSpacing?.({ target, ...espacement }, options),
      relanceLePeintre: RELANCE_LE_PEINTRE.espacement,
    }]),
    ...(bordure ?? []).map(({ geste, ...entree }) => ({
      famille: 'bordure',
      etiquette: `bordure-${entree.side}:${nodeId}`,
      appel: (options: unknown) => (geste === 'poser' ? surface?.setBorder?.({ target, ...entree }, options) : surface?.clearBorder?.({ target, ...entree }, options)),
      relanceLePeintre: RELANCE_LE_PEINTRE.bordure,
    })),
    ...(fond === undefined ? [] : [{
      famille: 'fond',
      etiquette: `fond:${nodeId}`,
      // Les trois attributs que Word écrit : l'éditeur remplace `w:shd` par ceux qu'on lui donne (`reglagesDeParagraphe.ts`, « Le fond »).
      appel: (options: unknown) => (fond.geste === 'poser'
        ? surface?.setShading?.({ target, fill: fond.couleur, color: 'auto', pattern: 'clear' }, options)
        : surface?.clearShading?.({ target }, options)),
      relanceLePeintre: RELANCE_LE_PEINTRE.fond,
    }]),
    ...(fondDeCellule === undefined ? [] : [{
      famille: 'fondDeCellule',
      etiquette: `fond-de-cellule:${fondDeCellule.cellule}`,
      appel: (options: unknown) => (fondDeCellule.geste === 'poser'
        ? tables?.setShading?.({ nodeId: fondDeCellule.cellule, color: fondDeCellule.couleur }, options)
        : tables?.clearShading?.({ nodeId: fondDeCellule.cellule }, options)),
      relanceLePeintre: RELANCE_LE_PEINTRE.fondDeCellule,
    }]),
    ...(cadreDeCase === undefined ? [] : [{
      famille: 'cadreDeCase',
      etiquette: `cadre-de-case:${cadreDeCase.cellule}`,
      // Mode `edges` : les seuls côtés à changer, en une écriture (`reglagesDeParagraphe.ts`, « Le cadre d'une case de tableau »).
      appel: (options: unknown) => tables?.setBorders?.({ nodeId: cadreDeCase.cellule, mode: 'edges', edges: cadreDeCase.cotes }, options),
      relanceLePeintre: RELANCE_LE_PEINTRE.cadreDeCase,
    }]),
  ];
}

/**
 * Soumet les écritures hors plan de chaque travail, dans l'ordre du document.
 * La série s'arrête à la première écriture refusée : ce qui est écrit le
 * reste. Une famille n'est dite faite qu'une fois sa DERNIÈRE écriture passée
 * — un paragraphe dont un côté manque n'a pas sa bordure —, mais elle est dite
 * appliquée dès sa première écriture qui change le paragraphe. Un travail
 * sauté n'est pas écrit.
 *
 * @param ecrituresDe Les écritures d'un travail, dans l'ordre où les soumettre, celles d'une même famille à la suite.
 * @returns Le nombre d'écritures à soumettre, travaux sautés exclus ; et, si la série est interrompue, le travail en
 *   cours avec le nombre d'écritures qu'il avait déjà reçues.
 */
export async function ecrireLaSerie<T extends Travail>(
  ecriture: Pick<EcritureDeFormeOuverte, 'ecrireUnParagraphe'>,
  travaux: readonly T[],
  ecrituresDe: (travail: T) => readonly EcritureHorsPlan[],
): Promise<{ prevues: number; interrompue?: { travail: T; recues: number } }> {
  const series = travaux.filter((travail) => travail.saute === undefined).map((travail) => ({ travail, ecritures: ecrituresDe(travail) }));
  const prevues = series.reduce((somme, serie) => somme + serie.ecritures.length, 0);
  for (const { travail, ecritures } of series) {
    for (const [rang, { famille, etiquette, appel, relanceLePeintre }] of ecritures.entries()) {
      const issue = await ecriture.ecrireUnParagraphe(etiquette, appel, relanceLePeintre);
      if (issue === 'refusee') return { prevues, interrompue: { travail, recues: rang } };
      if (issue === 'ecrite') travail.appliquees.add(famille);
      if (ecritures[rang + 1]?.famille !== famille) travail.faites.add(famille);
    }
  }
  return { prevues };
}

/** Ce qui est écrit, famille par famille, sur les travaux qui devaient recevoir cette écriture : l'avancement d'une écriture interrompue, en clair. */
export function direLAvancement<T extends Travail>(travaux: readonly T[], familles: ReadonlyArray<FamilleDeTravail<T> & { nom: string }>): string {
  return familles.flatMap(({ famille, nom, prevue }) => {
    const concernes = travaux.filter(prevue);
    return concernes.length === 0 ? [] : [`${nom} written on ${concernes.filter((travail) => travail.faites.has(famille)).length} of ${concernes.length} paragraphs`];
  }).join(', ');
}

/**
 * Les comptes que le reçu attend d'une écriture de paragraphes entiers : ce
 * qui était à écrire, les paragraphes ENTIÈREMENT écrits en tête ; leur
 * nombre ; et, s'il y en a, ceux qui ne le sont qu'en partie.
 *
 * @param aCompter Les travaux qui entrent dans les comptes de paragraphes écrits : ni sautés, ni laissés pour une écriture écartée d'avance.
 * @param avancement Ce qui est écrit, famille par famille, en clair ({@link direLAvancement}).
 */
export function compterLAvancement<T extends Travail>(
  aCompter: readonly T[],
  familles: ReadonlyArray<FamilleDeTravail<T>>,
  avancement: string,
): Pick<FaitsDeLEcritureDeForme, 'aEcrire' | 'etapesEcrites' | 'partiels'> {
  const entiers = aCompter.filter((travail) => estEntier(travail, familles));
  const nonEntiers = aCompter.filter((travail) => !estEntier(travail, familles));
  // En partie : une famille achevée, ou une famille entamée (une bordure dont un côté est posé).
  const partiels = nonEntiers.filter((travail) => travail.faites.size > 0 || travail.appliquees.size > 0).length;
  return {
    aEcrire: [...entiers, ...nonEntiers].map((travail) => travail.paragraphe),
    etapesEcrites: entiers.length,
    ...(partiels === 0 ? {} : { partiels: { nombre: partiels, avancement } }),
  };
}

/**
 * Les paragraphes écrits sur un changement de forme de paragraphe en attente,
 * à nommer au reçu (`ecritureDeForme.ts`, « Un paragraphe dont la forme attend
 * une décision ») : ceux dont une écriture de forme de PARAGRAPHE a réellement
 * changé le paragraphe. Une forme de texte seule, ou une écriture sans objet,
 * ne change pas ce que la révision propose : le paragraphe n'est pas nommé.
 *
 * @param surRevision Les paragraphes de la cible qui portent la révision.
 * @param deParagraphe Vrai si cette famille d'écriture, sur ce travail, écrit une forme de paragraphe.
 * @returns Leurs identifiants, dans l'ordre des travaux.
 */
export function ecritsSurUneRevisionDeParagraphe<T extends Travail>(
  travaux: readonly T[],
  surRevision: readonly ParagrapheCible[],
  deParagraphe: (travail: T, famille: string) => boolean,
): string[] {
  return travaux
    .filter((travail) => surRevision.includes(travail.paragraphe) && [...travail.appliquees].some((famille) => deParagraphe(travail, famille)))
    .map((travail) => travail.paragraphe.nodeId);
}
